import { useEffect, useState } from "react";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { updateDoc } from "firebase/firestore";

import {
  AccordionHeader,
  AccordionItem,
  AccordionPanel,
  Combobox,
  Field,
  Option,
} from "@fluentui/react-components";

import type { Interview } from "@aglocal/schema/Interview";

import { useMissionControlDB } from "@aglocal/web/firebase";

const LANGUAGE_LABELS = defineMessages({
  en: {
    defaultMessage: "English",
    description:
      "Transcription language option: the language's own name, followed by its name in the user's language in parentheses when they differ.",
  },
  es: {
    defaultMessage: "Español (Spanish)",
    description:
      "Transcription language option: the language's own name, followed by its name in the user's language in parentheses when they differ.",
  },
  fr: {
    defaultMessage: "Français (French)",
    description:
      "Transcription language option: the language's own name, followed by its name in the user's language in parentheses when they differ.",
  },
  de: {
    defaultMessage: "Deutsch (German)",
    description:
      "Transcription language option: the language's own name, followed by its name in the user's language in parentheses when they differ.",
  },
  it: {
    defaultMessage: "Italiano (Italian)",
    description:
      "Transcription language option: the language's own name, followed by its name in the user's language in parentheses when they differ.",
  },
});

type LanguageCode = keyof typeof LANGUAGE_LABELS;

const LANGUAGE_CODES = Object.keys(LANGUAGE_LABELS) as LanguageCode[];

const DEFAULT_LANGUAGE: LanguageCode = "en";

function isLanguageCode(value: string): value is LanguageCode {
  return Object.hasOwn(LANGUAGE_LABELS, value);
}

export interface LanguageAccordionItemProps {
  name: unknown;
  interview: Interview;
}

export default function LanguageAccordionItem({
  name,
  interview,
}: LanguageAccordionItemProps) {
  const { $t } = useIntl();
  const db = useMissionControlDB();

  const defaultLanguage =
    interview.selectedLanguage ?? interview.detectedLanguage ?? null;

  const [language, setLanguage] = useState(defaultLanguage);
  const selected = language ?? DEFAULT_LANGUAGE;

  useEffect(() => {
    setLanguage((currentLanguage) => currentLanguage ?? defaultLanguage);
  }, [defaultLanguage]);

  const getLabel = (code: string) =>
    isLanguageCode(code) ? $t(LANGUAGE_LABELS[code]) : code;

  const updateLanguage = (value: string) => {
    setLanguage(value);
    void updateDoc(db.interviews.doc(interview.id), {
      selectedLanguage: value,
    });
  };

  return (
    <AccordionItem value={name}>
      <AccordionHeader>
        <FormattedMessage
          defaultMessage="Language Selection"
          description="Header of the in-meeting sidebar section where the recruiter picks the language spoken in the meeting."
        />
      </AccordionHeader>
      <AccordionPanel>
        <Field
          label={$t({
            defaultMessage: "Language",
            description: "Label of the meeting language dropdown.",
          })}
          hint={
            interview.detectedLanguage &&
            $t(
              {
                defaultMessage: "Detected language: {language}",
                description:
                  "Hint under the language dropdown; {language} is the language automatically detected in the conversation.",
              },
              { language: getLabel(interview.detectedLanguage) },
            )
          }
        >
          <Combobox
            value={getLabel(selected)}
            selectedOptions={[selected]}
            onOptionSelect={(event, { optionValue }) => {
              if (optionValue) updateLanguage(optionValue);
            }}
          >
            {LANGUAGE_CODES.map((code) => (
              <Option key={code} value={code} text={getLabel(code)}>
                {getLabel(code)}
              </Option>
            ))}
          </Combobox>
        </Field>
      </AccordionPanel>
    </AccordionItem>
  );
}
